/**
 * Entity metadata-schema display annotations, read the same way on every
 * surface (record page, edit form, table columns, collection filters).
 *
 * - `x-hidden: true` — omits the field from schema-driven field lists.
 * - `x-enum-labels` — maps a stringified enum value to the label it displays as.
 */
type AnnotatedProperty = { "x-hidden"?: unknown; "x-enum-labels"?: unknown };

export function isHiddenSchemaProperty(prop: unknown): boolean {
	return (
		typeof prop === "object" &&
		prop !== null &&
		(prop as AnnotatedProperty)["x-hidden"] === true
	);
}

/** A schema's `properties` entries without its `x-hidden` fields, in order. */
export function visibleSchemaEntries<T>(
	properties: Record<string, T> | undefined,
): [string, T][] {
	return Object.entries(properties ?? {}).filter(
		([, prop]) => !isHiddenSchemaProperty(prop),
	);
}

/** The `x-enum-labels` label for an enum value, or `undefined` when none. */
export function schemaEnumLabel(
	prop: unknown,
	value: unknown,
): string | undefined {
	if (typeof prop !== "object" || prop === null) return undefined;
	const labels = (prop as AnnotatedProperty)["x-enum-labels"];
	if (typeof labels !== "object" || labels === null) return undefined;
	const label = (labels as Record<string, unknown>)[String(value)];
	return typeof label === "string" ? label : undefined;
}
